import type { TriggerConfig } from "./types";
import { storageGet, storageSet } from "./utils";

function frequencyStore(frequency: TriggerConfig["frequency"]): Storage {
  return frequency === "session" ? sessionStorage : localStorage;
}

function alreadyShown(formId: string, trigger: TriggerConfig): boolean {
  if (!trigger.frequency || trigger.frequency === "always") return false;
  return storageGet(frequencyStore(trigger.frequency), `hrn_forms_seen_${formId}`) === "1";
}

function markShown(formId: string, trigger: TriggerConfig): void {
  if (!trigger.frequency || trigger.frequency === "always") return;
  storageSet(frequencyStore(trigger.frequency), `hrn_forms_seen_${formId}`, "1");
}

export function alreadySubmitted(formId: string): boolean {
  return storageGet(localStorage, `hrn_forms_submitted_${formId}`) === "1";
}

export function markSubmitted(formId: string): void {
  storageSet(localStorage, `hrn_forms_submitted_${formId}`, "1");
}

/** Calls `show()` once the configured trigger condition is met. */
export function armTrigger(formId: string, trigger: TriggerConfig, show: () => void): void {
  if (alreadySubmitted(formId) || alreadyShown(formId, trigger)) return;

  const fire = () => {
    markShown(formId, trigger);
    show();
  };

  switch (trigger.type) {
    case "inline":
      // handled by the caller directly (renders into a target element, no arming needed)
      return;

    case "immediate":
      fire();
      return;

    case "delay": {
      const ms = trigger.delayMs ?? 5000;
      window.setTimeout(fire, ms);
      return;
    }

    case "scroll": {
      const threshold = trigger.scrollPercent ?? 50;
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const doc = document.documentElement;
          const scrollable = doc.scrollHeight - doc.clientHeight;
          const percent = scrollable > 0 ? (doc.scrollTop / scrollable) * 100 : 100;
          if (percent >= threshold) {
            window.removeEventListener("scroll", onScroll);
            fire();
          }
        });
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return;
    }

    case "exit-intent": {
      const onMouseOut = (e: MouseEvent) => {
        if (e.clientY > 0 || (e.relatedTarget as Node | null) !== null) return;
        document.removeEventListener("mouseout", onMouseOut);
        fire();
      };
      document.addEventListener("mouseout", onMouseOut);
      return;
    }
  }
}
